/**
 * API Client Service for Bursaku POS + CRM Backend (api.srjmkr.test)
 */

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'https://api.srjmkr.test';

export function storageUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  if (path.startsWith('/')) return `${API_BASE_URL}${path}`;
  if (path.startsWith('storage/')) return `${API_BASE_URL}/${path}`;
  return `${API_BASE_URL}/storage/${path}`;
}

/**
 * Fetchable URL for a storage file, routed through the API `/files`
 * endpoint so the response carries CORS headers. Use this for cross-origin
 * `fetch()` (PDF export embedding); keep `storageUrl()` for `<img>` tags.
 * @param {string|null|undefined} path `storage/...` or a bare relative path
 * @returns {string|null}
 */
export function fileFetchUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  const relative = String(path).replace(/^\/+/, '').replace(/^storage\//i, '');
  return `${API_BASE_URL}/files?path=${encodeURIComponent(`storage/${relative}`)}`;
}

export async function apiFetch(endpoint, options = {}) {
  const token = localStorage.getItem('auth_token');

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const config = {
    ...options,
    headers,
  };

  if (typeof FormData !== 'undefined' && config.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    // Proxy/web-server rejections never reach Laravel, so there is no JSON message to show
    const gatewayMessages = {
      413: 'Ukuran file melebihi batas unggah server. Hubungi admin untuk menaikkan batas upload.',
      502: 'Server tidak merespons (502). Coba lagi beberapa saat.',
      504: 'Server terlalu lama merespons (504). Coba lagi beberapa saat.',
    };
    const errorMsg = data?.message || gatewayMessages[response.status] || `Request failed with status ${response.status}`;
    const error = new Error(errorMsg);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function loginApi(username, password, remember = false) {
  const result = await apiFetch('/login', {
    method: 'POST',
    body: JSON.stringify({ username, password, remember }),
  });

  if (result?.data?.token) {
    localStorage.setItem('auth_token', result.data.token);
    if (result?.data?.user) {
      localStorage.setItem('auth_user', JSON.stringify(result.data.user));
    }
  }

  return result;
}

export async function getProfileApi() {
  return await apiFetch('/me', { method: 'GET' });
}

function syncAuthUser(payload) {
  if (!payload?.user) return;
  localStorage.setItem('auth_user', JSON.stringify(payload.user));
}

export async function updateProfileApi(payload) {
  const result = await apiFetch('/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  syncAuthUser(result?.data);
  return result;
}

export async function changePasswordApi(payload) {
  return await apiFetch('/profile/password', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function uploadProfilePictureApi(file) {
  const formData = new FormData();
  formData.append('picture', file);
  const result = await apiFetch('/profile/picture', {
    method: 'POST',
    body: formData,
  });
  syncAuthUser(result?.data);
  return result;
}

export async function logoutApi() {
  try {
    await apiFetch('/logout', { method: 'POST' });
  } finally {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  }
}

// User management (Pengaturan → Pengguna) — superadmin only
export async function getManagedUsersApi() {
  const result = await apiFetch('/settings/users', { method: 'GET' });
  return result?.data ?? [];
}

export async function getRolesApi() {
  const result = await apiFetch('/settings/roles', { method: 'GET' });
  return result?.data ?? [];
}

export async function createManagedUserApi(payload) {
  const result = await apiFetch('/settings/users', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateManagedUserRoleApi(id, roleId) {
  const result = await apiFetch(`/settings/users/${id}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role: roleId }),
  });
  return result?.data ?? null;
}

export async function resetManagedUserPasswordApi(id) {
  const result = await apiFetch(`/settings/users/${id}/reset-password`, { method: 'POST' });
  return result?.data?.temporary_password ?? null;
}

export async function deleteManagedUserApi(id) {
  return await apiFetch(`/settings/users/${id}`, { method: 'DELETE' });
}

export async function checkPingApi() {
  return await apiFetch('/ping', { method: 'GET' });
}

export async function uploadFileApi(file, folder = 'uploads') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);
  const result = await apiFetch('/upload', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function getSuppliersApi() {
  const result = await apiFetch('/suppliers', { method: 'GET' });
  return result?.data ?? [];
}

export async function getSupplierApi(id) {
  const result = await apiFetch(`/suppliers/${id}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function createSupplierApi(payload) {
  const result = await apiFetch('/suppliers/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateSupplierApi(id, payload) {
  const result = await apiFetch(`/suppliers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteSupplierApi(id) {
  const result = await apiFetch(`/suppliers/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function getCustomersApi() {
  const result = await apiFetch('/customers', { method: 'GET' });
  return result?.data ?? [];
}

export async function getCustomerApi(id) {
  const result = await apiFetch(`/customers/${id}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function createCustomerApi(payload) {
  const result = await apiFetch('/customers/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateCustomerApi(id, payload) {
  const result = await apiFetch(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteCustomerApi(id) {
  const result = await apiFetch(`/customers/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function getProductsApi() {
  const result = await apiFetch('/products', { method: 'GET' });
  return result?.data ?? [];
}

export async function getNextProductCodeApi() {
  const result = await apiFetch('/products/next-code', { method: 'GET' });
  return result?.data?.product_code ?? null;
}

export async function getCategoriesApi() {
  const result = await apiFetch('/products/categories', { method: 'GET' });
  return result?.data ?? [];
}

/** payload: { display_name?, description?, banner_image? } — banner_image null removes the banner. */
export async function updateCategoryApi(id, payload) {
  const result = await apiFetch(`/products/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function uploadCategoryBannerApi(file, catId) {
  const formData = new FormData();
  formData.append('banner', file);
  if (catId != null) formData.append('cat_id', catId);
  const result = await apiFetch('/products/categories/upload-banner', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function getUnitsApi() {
  const result = await apiFetch('/product-units', { method: 'GET' });
  return result?.data ?? [];
}

export async function createUnitApi(payload) {
  const result = await apiFetch('/product-units', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateUnitApi(id, payload) {
  const result = await apiFetch(`/product-units/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteUnitApi(id) {
  const result = await apiFetch(`/product-units/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function getProductTypesApi() {
  const result = await apiFetch('/product-types', { method: 'GET' });
  return result?.data ?? [];
}

export async function createProductTypeApi(payload) {
  const result = await apiFetch('/product-types', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function setProductTypeActiveApi(id, isActive) {
  const result = await apiFetch(`/product-types/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  });
  return result?.data ?? null;
}

export async function bulkSetProductTypesActiveApi(ids, isActive) {
  const result = await apiFetch('/product-types/bulk-status', {
    method: 'POST',
    body: JSON.stringify({ ids, is_active: isActive }),
  });
  return result?.data ?? null;
}

export async function bulkDeleteProductTypesApi(ids) {
  const result = await apiFetch('/product-types/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  return result?.data ?? null;
}

export async function deleteProductTypeApi(id) {
  const result = await apiFetch(`/product-types/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function getProductSkusApi(productCode) {
  const result = await apiFetch(`/products/${productCode}/skus`, { method: 'GET' });
  return result?.data ?? [];
}

export async function duplicateCheckApi(name, categoryHint = null, options = {}) {
  const body = { product_name: name };
  if (categoryHint != null && categoryHint !== '') body.product_category = categoryHint;
  if (options.limit) body.limit = options.limit;
  if (options.threshold != null) body.threshold = options.threshold;
  const result = await apiFetch('/products/duplicate-check', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return result?.data ?? [];
}

export async function uploadProductPictureApi(file, productCode = 'product') {
  const formData = new FormData();
  formData.append('pict', file);
  formData.append('product_code', productCode);
  const result = await apiFetch('/products/upload-picture', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function createProductApi(payload) {
  const result = await apiFetch('/products/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateProductApi(id, payload) {
  const result = await apiFetch(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteProductApi(id) {
  const result = await apiFetch(`/products/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function getPurchasesApi() {
  const result = await apiFetch('/purchases', { method: 'GET' });
  return result?.data ?? [];
}

export async function getPurchaseNextCodeApi() {
  const result = await apiFetch('/purchases/next-code', { method: 'GET' });
  return result?.data?.trxno ?? null;
}

export async function createPurchaseApi(payload) {
  const result = await apiFetch('/purchases/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updatePurchaseApi(id, payload) {
  const result = await apiFetch(`/purchases/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deletePurchaseApi(id) {
  const result = await apiFetch(`/purchases/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function uploadPurchaseReceiptApi(file, trxno = 'purchase') {
  const formData = new FormData();
  formData.append('receipt', file);
  formData.append('trxno', trxno);
  const result = await apiFetch('/purchases/upload-receipt', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function uploadPurchaseAttachmentApi(file, trxno = 'purchase') {
  const formData = new FormData();
  formData.append('attachment', file);
  formData.append('trxno', trxno);
  const result = await apiFetch('/purchases/upload-attachment', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

/**
 * Build a query string from params. Array values (e.g. `cust_code`, `pay_status`)
 * are serialized as repeated `key[]=value` so Laravel receives an array.
 */
export function buildQueryString(params) {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        qs.append(`${key}[]`, item);
      }
    } else {
      qs.append(key, value);
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

export async function getSalesApi(params) {
  const result = await apiFetch(`/sales${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getSalesSummaryApi(params) {
  const result = await apiFetch(`/sales/summary${buildQueryString(params)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getSaleApi(id) {
  const result = await apiFetch(`/sales/${id}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getSalesNextCodeApi() {
  const result = await apiFetch('/sales/next-code', { method: 'GET' });
  return result?.data?.trxno ?? null;
}

export async function createSaleApi(payload) {
  const result = await apiFetch('/sales/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteSaleApi(id) {
  const result = await apiFetch(`/sales/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function uploadSalesReceiptApi(file, trxno = 'sale') {
  const formData = new FormData();
  formData.append('receipt', file);
  formData.append('trxno', trxno);
  const result = await apiFetch('/sales/upload-receipt', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function getCataloguesApi(params) {
  const result = await apiFetch(`/catalogues${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getCataloguesSummaryApi(params) {
  const result = await apiFetch(`/catalogues/summary${buildQueryString(params)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getCatalogueApi(code) {
  const result = await apiFetch(`/catalogues/${encodeURIComponent(code)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getCataloguesNextCodeApi() {
  const result = await apiFetch('/catalogues/next-code', { method: 'GET' });
  return result?.data?.catalogue_code ?? null;
}

export async function createCatalogueApi(payload) {
  const result = await apiFetch('/catalogues/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateCatalogueApi(code, payload) {
  const result = await apiFetch(`/catalogues/${encodeURIComponent(code)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteCatalogueApi(code) {
  const result = await apiFetch(`/catalogues/${encodeURIComponent(code)}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function getCatalogueItemsApi(code) {
  const result = await apiFetch(`/catalogues/${encodeURIComponent(code)}/items`, { method: 'GET' });
  return result?.data ?? [];
}

export async function createCatalogueItemApi(code, payload) {
  const result = await apiFetch(`/catalogues/${encodeURIComponent(code)}/items`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function addCatalogueItemsFromBatchApi(code, batchItemIds) {
  const result = await apiFetch(`/catalogues/${encodeURIComponent(code)}/items/from-batch-items`, {
    method: 'POST',
    body: JSON.stringify({ batch_item_ids: batchItemIds }),
  });
  return result?.data ?? [];
}

export async function deleteCatalogueItemApi(itemId) {
  const result = await apiFetch(`/catalogues/items/${itemId}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function deleteCatalogueItemsBySupplierApi(supplier) {
  const result = await apiFetch(
    `/catalogues/items/supplier/${encodeURIComponent(supplier)}`,
    { method: 'DELETE' },
  );
  return result?.data ?? null;
}

// "Semua Produk": every item across all (legacy) catalogues. Returns { data, pagination }.
export async function getCatalogueProductsApi(params = {}) {
  const result = await apiFetch(`/catalogues/products${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function updateCatalogueItemApi(itemId, payload) {
  const result = await apiFetch(`/catalogues/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function bulkUpdateCatalogueItemsApi(payload) {
  const result = await apiFetch('/catalogues/items/bulk', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function bulkDeleteCatalogueItemsApi(itemIds) {
  const result = await apiFetch('/catalogues/items/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ item_ids: itemIds }),
  });
  return result?.data ?? null;
}

export async function uploadCataloguePictureApi(file, catalogueCode = 'catalogue') {
  const formData = new FormData();
  formData.append('pict', file);
  formData.append('catalogue_code', catalogueCode);
  const result = await apiFetch('/catalogues/upload-picture', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

/**
 * Fetch a catalogue/promotion through the server-side PDF pipeline. The
 * Laravel endpoint renders the print route in headless Chromium and
 * streams the result back as a PDF Blob plus the attachment filename.
 * @param {'catalogue'|'promotion'} kind
 * @param {object} body `{ code }` for catalogue or `{ slug }` for promotion
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export async function exportPdfApi(kind, body) {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/pdf/${kind}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const error = new Error(data?.message || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  const blob = await response.blob();
  const filename =
    filenameFromDisposition(response) ?? `${kind === 'catalogue' ? 'Katalog' : 'Promo'}_Seroja.pdf`;
  return { blob, filename };
}

function filenameFromDisposition(response) {
  const disposition = response.headers.get('Content-Disposition') || '';
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  return (utfMatch ? decodeURIComponent(utfMatch[1]) : null) ?? (plainMatch ? plainMatch[1].trim() : null);
}

/**
 * Load the print payload (same shape as `/catalogues/{code}` /
 * `/promotions/{idOrSlug}`) for the public, signature-gated print route.
 * Only reachable with a short-lived `exp` + `sig` signed query string.
 */
export async function getCataloguePrintDataApi(code, { exp, sig }) {
  const query = `exp=${encodeURIComponent(exp)}&sig=${encodeURIComponent(sig)}`;
  const result = await apiFetch(`/print/catalogue/${encodeURIComponent(code)}?${query}`, {
    method: 'GET',
  });
  return result?.data ?? null;
}

export async function getPromotionPrintDataApi(idOrSlug, { exp, sig }) {
  const query = `exp=${encodeURIComponent(exp)}&sig=${encodeURIComponent(sig)}`;
  const result = await apiFetch(`/print/promotion/${encodeURIComponent(idOrSlug)}?${query}`, {
    method: 'GET',
  });
  return result?.data ?? null;
}

export async function getPromotionsApi(params) {
  const result = await apiFetch(`/promotions${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getPromotionNextCodeApi() {
  const result = await apiFetch('/promotions/next-code', { method: 'GET' });
  return result?.data?.promotion_code ?? result?.data?.slug ?? null;
}

export async function getPromotionApi(idOrSlug) {
  const result = await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getPromotionItemsApi(idOrSlug) {
  const result = await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}/items`, { method: 'GET' });
  return result?.data ?? [];
}

export async function createPromotionApi(payload) {
  const result = await apiFetch('/promotions/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updatePromotionApi(idOrSlug, payload) {
  const result = await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

/** Makes a new draft-preview secret (the old link stops working). Returns the token. */
export async function createPromotionPreviewLinkApi(idOrSlug) {
  const result = await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}/preview-link`, { method: 'POST' });
  return result?.data?.preview_token ?? null;
}

export async function revokePromotionPreviewLinkApi(idOrSlug) {
  await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}/preview-link`, { method: 'DELETE' });
}

export async function deletePromotionApi(idOrSlug) {
  const result = await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function createPromotionItemApi(idOrSlug, payload) {
  const result = await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}/items`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deletePromotionItemApi(itemId) {
  const result = await apiFetch(`/promotions/items/${itemId}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function bulkUpdatePromotionItemPricesApi(idOrSlug, payload) {
  const result = await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}/items/bulk-price`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updatePromotionItemApi(itemId, payload) {
  const result = await apiFetch(`/promotions/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

/** Catalogue (New) products a promotion can be built from. params: { keyword, category } */
export async function getPromotionCatalogueProductsApi(params) {
  const result = await apiFetch(`/promotions/catalogue-products${buildQueryString(params)}`, { method: 'GET' });
  return result?.data ?? [];
}

/** Add a real catalogue product to a promotion at a promo price (data is copied server-side). */
export async function addPromotionItemFromCatalogueApi(idOrSlug, payload) {
  const result = await apiFetch(`/promotions/${encodeURIComponent(idOrSlug)}/items/from-catalogue`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function uploadPromotionPosterApi(file, promotionId = 'promo') {
  const formData = new FormData();
  formData.append('poster', file);
  formData.append('promotion_id', promotionId);
  const result = await apiFetch('/promotions/upload-poster', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function uploadPromotionItemPictureApi(file, promotionId = 'item') {
  const formData = new FormData();
  formData.append('pict', file);
  formData.append('promotion_id', promotionId);
  const result = await apiFetch('/promotions/upload-item-picture', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function getHighlightsApi(params) {
  const result = await apiFetch(`/highlights${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getHighlightApi(idOrSlug) {
  const result = await apiFetch(`/highlights/${encodeURIComponent(idOrSlug)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getHighlightProductsApi(idOrSlug) {
  const result = await apiFetch(`/highlights/${encodeURIComponent(idOrSlug)}/products`, { method: 'GET' });
  return result?.data ?? [];
}

export async function createHighlightApi(payload) {
  const result = await apiFetch('/highlights/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateHighlightApi(idOrSlug, payload) {
  const result = await apiFetch(`/highlights/${encodeURIComponent(idOrSlug)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateHighlightStatusApi(idOrSlug, payload = {}) {
  const result = await apiFetch(`/highlights/${encodeURIComponent(idOrSlug)}/status`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteHighlightApi(idOrSlug) {
  const result = await apiFetch(`/highlights/${encodeURIComponent(idOrSlug)}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function createHighlightProductApi(idOrSlug, payload) {
  const result = await apiFetch(`/highlights/${encodeURIComponent(idOrSlug)}/products`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateHighlightProductApi(productId, payload) {
  const result = await apiFetch(`/highlights/products/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteHighlightProductApi(productId) {
  const result = await apiFetch(`/highlights/products/${productId}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function uploadHighlightMediaApi(file, highlightId = 'highlight') {
  const formData = new FormData();
  formData.append('media', file);
  formData.append('highlight_id', highlightId);
  const result = await apiFetch('/highlights/upload-media', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function uploadHighlightPosterApi(file, highlightId = 'highlight') {
  const formData = new FormData();
  formData.append('poster', file);
  formData.append('highlight_id', highlightId);
  const result = await apiFetch('/highlights/upload-poster', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function uploadHighlightProductPictureApi(file, highlightId = 'item') {
  const formData = new FormData();
  formData.append('pict', file);
  formData.append('highlight_id', highlightId);
  const result = await apiFetch('/highlights/upload-product-picture', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

// ============================================================
// FINANCE SUITE: Expense, Debt (Hutang), Credit (Piutang)
// ============================================================

// --- Expense ---
export async function getExpensesApi(params) {
  const result = await apiFetch(`/expenses${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getExpensesSummaryApi(params) {
  const result = await apiFetch(`/expenses/summary${buildQueryString(params)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getExpenseNextCodeApi() {
  const result = await apiFetch('/expenses/next-code', { method: 'GET' });
  return result?.data?.expense_no ?? null;
}

export async function createExpenseApi(payload) {
  const result = await apiFetch('/expenses/store', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateExpenseApi(id, payload) {
  const result = await apiFetch(`/expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateExpensePaymentStatusApi(id, payStatus) {
  const result = await apiFetch(`/expenses/${id}/payment-status`, {
    method: 'PUT',
    body: JSON.stringify({ pay_status: payStatus }),
  });
  return result?.data ?? null;
}

export async function deleteExpenseApi(id) {
  const result = await apiFetch(`/expenses/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function uploadExpenseReceiptApi(file, expenseNo = 'expense') {
  const formData = new FormData();
  formData.append('receipt', file);
  formData.append('expense_no', expenseNo);
  const result = await apiFetch('/expenses/upload-receipt', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

export async function getExpenseCategoriesApi() {
  const result = await apiFetch('/expenses/categories', { method: 'GET' });
  return result?.data ?? [];
}

export async function getExpenseCategoryListApi() {
  const result = await apiFetch('/expense-categories', { method: 'GET' });
  return result?.data ?? [];
}

export async function createExpenseCategoryApi(payload) {
  const result = await apiFetch('/expense-categories', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function updateExpenseCategoryApi(id, payload) {
  const result = await apiFetch(`/expense-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteExpenseCategoryApi(id) {
  const result = await apiFetch(`/expense-categories/${id}`, { method: 'DELETE' });
  return result?.data ?? null;
}

// --- Dashboard ---
export async function getDashboardApi() {
  const result = await apiFetch('/dashboard', { method: 'GET' });
  return result?.data ?? null;
}

// --- Reports ---
export async function getMonthlyReportApi(month) {
  const result = await apiFetch(`/reports/monthly${buildQueryString({ month })}`, { method: 'GET' });
  return result?.data ?? null;
}

// --- Site Analytics (Storefront) ---
export async function getAnalyticsOverviewApi(params) {
  const result = await apiFetch(`/analytics/overview${buildQueryString(params)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getAnalyticsPopularProductsApi(params) {
  const result = await apiFetch(`/analytics/popular-products${buildQueryString(params)}`, {
    method: 'GET',
  });
  return result?.data ?? [];
}

export async function getAnalyticsTopSearchesApi(params) {
  const result = await apiFetch(`/analytics/top-searches${buildQueryString(params)}`, {
    method: 'GET',
  });
  return result?.data ?? [];
}

export async function getAnalyticsTrafficChartApi(params) {
  const result = await apiFetch(`/analytics/traffic-chart${buildQueryString(params)}`, {
    method: 'GET',
  });
  return result?.data ?? [];
}

export async function getAnalyticsPagesApi(params) {
  const result = await apiFetch(`/analytics/pages${buildQueryString(params)}`, { method: 'GET' });
  return result?.data ?? { pages: [], summary: {} };
}

/**
 * Download the visitor report as a file. `format`: 'xlsx' (one sheet per
 * table), 'json' (full report) or 'md' (readable summary).
 * Sent with the bearer header so the token never lands in a URL.
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export async function exportAnalyticsApi(params = {}) {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/analytics/export${buildQueryString(params)}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const error = new Error(data?.message || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  const blob = await response.blob();
  const filename = filenameFromDisposition(response) ?? `analitik-pengunjung.${params.format ?? 'xlsx'}`;
  return { blob, filename };
}

// --- Debt (Hutang Supplier) ---
export async function getDebtsApi(params) {
  const result = await apiFetch(`/debts${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getDebtsSummaryApi() {
  const result = await apiFetch('/debts/summary', { method: 'GET' });
  return result?.data ?? null;
}

export async function settleDebtApi(id) {
  const result = await apiFetch(`/debts/${id}/settle`, {
    method: 'PUT',
    body: JSON.stringify({}),
  });
  return result?.data ?? null;
}

// --- Credit (Piutang Customer) ---
export async function getCreditsApi(params) {
  const result = await apiFetch(`/credits${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getCreditsSummaryApi() {
  const result = await apiFetch('/credits/summary', { method: 'GET' });
  return result?.data ?? null;
}

export async function getCreditApi(id) {
  const result = await apiFetch(`/credits/${id}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function createCreditPaymentApi(id, payload) {
  const result = await apiFetch(`/credits/${id}/payments`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteCreditPaymentApi(paymentId) {
  const result = await apiFetch(`/credits/payments/${paymentId}`, { method: 'DELETE' });
  return result?.data ?? null;
}

export async function uploadCreditReceiptApi(file) {
  const formData = new FormData();
  formData.append('receipt', file);
  const result = await apiFetch('/credits/upload-receipt', {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

// ── BC Batches (Catalogue New) ──────────────────────────────
export async function getBcBatchesApi(params = {}) {
  const result = await apiFetch(`/bc-batches${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getBcBatchesSummaryApi(params = {}) {
  const result = await apiFetch(`/bc-batches/summary${buildQueryString(params)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getBcBatchCuratedItemsApi(params = {}) {
  const result = await apiFetch(`/bc-batches/curation/items${buildQueryString(params)}`, { method: 'GET' });
  return result ?? {};
}

export async function getSuggestedBatchNameApi(date, hour) {
  const params = {};
  if (date) params.date = date;
  if (hour != null) params.hour = hour;
  const result = await apiFetch(`/bc-batches/generate-name${buildQueryString(params)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getBcBatchNextCodeApi(date) {
  const params = date ? { date } : {};
  const result = await apiFetch(`/bc-batches/next-code${buildQueryString(params)}`, { method: 'GET' });
  return result?.data?.batch_code ?? null;
}

export async function createBcBatchApi(payload) {
  const result = await apiFetch('/bc-batches', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function getBcBatchDetailApi(id) {
  const result = await apiFetch(`/bc-batches/${encodeURIComponent(id)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function updateBcBatchApi(id, payload) {
  const result = await apiFetch(`/bc-batches/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteBcBatchApi(id) {
  const result = await apiFetch(`/bc-batches/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return result ?? null;
}

export async function getBcBatchItemsApi(batchCode) {
  const result = await apiFetch(`/bc-batches/${encodeURIComponent(batchCode)}/items`, { method: 'GET' });
  return result?.data ?? [];
}

export async function createBcBatchItemApi(batchCode, payload) {
  const result = await apiFetch(`/bc-batches/${encodeURIComponent(batchCode)}/items`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function deleteBcBatchItemApi(itemId) {
  const result = await apiFetch(`/bc-batches/items/${itemId}`, { method: 'DELETE' });
  return result ?? null;
}

export async function deleteBcBatchItemsBySupplierApi(supplier, batchCode) {
  const params = batchCode ? { batch_code: batchCode } : {};
  const result = await apiFetch(
    `/bc-batches/items/supplier/${encodeURIComponent(supplier)}${buildQueryString(params)}`,
    { method: 'DELETE' },
  );
  return result ?? null;
}

export async function updateBcBatchItemApi(itemId, payload) {
  const result = await apiFetch(`/bc-batches/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function bulkUpdateBcBatchItemsApi(batchCode, payload) {
  const result = await apiFetch(`/bc-batches/${encodeURIComponent(batchCode)}/items/bulk`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? [];
}

export async function publishBcBatchItemApi(itemId, isPublished) {
  const result = await apiFetch(`/bc-batches/items/${itemId}/publish`, {
    method: 'PATCH',
    body: JSON.stringify({ is_published: isPublished }),
  });
  return result?.data ?? null;
}

export async function bulkPublishBcBatchItemsApi(batchCode, itemIds, isPublished) {
  const result = await apiFetch(`/bc-batches/${encodeURIComponent(batchCode)}/items/bulk-publish`, {
    method: 'PATCH',
    body: JSON.stringify({ item_ids: itemIds, is_published: isPublished }),
  });
  return result?.data ?? null;
}

/**
 * Gemini AI API (text generation)
 */
export async function generateGeminiTextApi({ prompt, model, system_instruction, item_id }) {
  const result = await apiFetch('/gemini/generate', {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      model: model || undefined,
      system_instruction: system_instruction || undefined,
      item_id: item_id || undefined,
    }),
  });
  return result?.data ?? null;
}

/**
 * AI Provider Settings API (superadmin only) — provider-agnostic.
 * `provider` is the registry key from the backend (e.g. "gemini").
 */
export async function getAiProvidersApi() {
  const result = await apiFetch('/ai/providers', { method: 'GET' });
  return result?.data ?? [];
}

export async function getAiProviderApi(provider) {
  const result = await apiFetch(`/ai/providers/${encodeURIComponent(provider)}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function updateAiProviderApi(provider, payload) {
  const result = await apiFetch(`/ai/providers/${encodeURIComponent(provider)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

export async function getAiProviderModelsApi(provider, { refresh = false } = {}) {
  const query = refresh ? '?refresh=1' : '';
  const result = await apiFetch(`/ai/providers/${encodeURIComponent(provider)}/models${query}`, { method: 'GET' });
  return result?.data ?? { default_model: null, models: [] };
}

export async function getAiProviderUsageApi(provider, model) {
  const query = model ? `?model=${encodeURIComponent(model)}` : '';
  const result = await apiFetch(`/ai/providers/${encodeURIComponent(provider)}/usage${query}`, { method: 'GET' });
  return result?.data ?? null;
}

/** WhatsApp Channel / Komunitas links shown on the website (any number, optionally per category). */
export async function getJoinLinksApi() {
  const result = await apiFetch('/settings/join-links', { method: 'GET' });
  return result?.data ?? [];
}

export async function createJoinLinkApi(payload) {
  const result = await apiFetch('/settings/join-links', { method: 'POST', body: JSON.stringify(payload) });
  return result?.data ?? null;
}

export async function updateJoinLinkApi(id, payload) {
  const result = await apiFetch(`/settings/join-links/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  return result?.data ?? null;
}

export async function deleteJoinLinkApi(id) {
  await apiFetch(`/settings/join-links/${id}`, { method: 'DELETE' });
}

/** Meta Pixel ID (superadmin only). */
export async function getMetaPixelSettingApi() {
  const result = await apiFetch('/settings/marketing/pixel', { method: 'GET' });
  return result?.data ?? null;
}

export async function updateMetaPixelSettingApi(pixelId) {
  const result = await apiFetch('/settings/marketing/pixel', {
    method: 'PUT',
    body: JSON.stringify({ meta_pixel_id: pixelId }),
  });
  return result?.data ?? null;
}

/**
 * WhatsApp Gateway Settings API (superadmin only) — Libericano URL, API key and
 * webhook signing secret. Secrets come back masked; an empty field on update keeps the
 * stored value, `clear_*` flags fall back to the server .env.
 */
export async function getWhatsAppGatewaySettingsApi() {
  const result = await apiFetch('/whatsapp/gateway', { method: 'GET' });
  return result?.data ?? null;
}

export async function updateWhatsAppGatewaySettingsApi(payload) {
  const result = await apiFetch('/whatsapp/gateway', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result?.data ?? null;
}

/**
 * ── WhatsApp Device API (Libericano Gateway) ─────────────────
 */
export async function getWhatsAppDeviceApi() {
  const result = await apiFetch('/whatsapp/device', { method: 'GET' });
  return result?.data ?? null;
}

export async function initWhatsAppDeviceApi(name) {
  const result = await apiFetch('/whatsapp/device/init', {
    method: 'POST',
    body: JSON.stringify(name ? { name } : {}),
  });
  return result?.data ?? null;
}

export async function pairWhatsAppQrApi() {
  const result = await apiFetch('/whatsapp/device/pair-qr', { method: 'POST' });
  return result?.data ?? null;
}

export async function pairWhatsAppCodeApi(phoneNumber) {
  const result = await apiFetch('/whatsapp/device/pair-code', {
    method: 'POST',
    body: JSON.stringify({ phone_number: phoneNumber }),
  });
  return result?.data ?? null;
}

export async function connectWhatsAppDeviceApi() {
  const result = await apiFetch('/whatsapp/device/connect', { method: 'POST' });
  return result?.data ?? null;
}

export async function wakeWhatsAppDeviceApi() {
  const result = await apiFetch('/whatsapp/device/wake', { method: 'POST' });
  return result?.data ?? null;
}

export async function unpairWhatsAppDeviceApi() {
  const result = await apiFetch('/whatsapp/device/unpair', { method: 'POST' });
  return result?.data ?? null;
}

export async function deleteWhatsAppDeviceApi() {
  const result = await apiFetch('/whatsapp/device', { method: 'DELETE' });
  return result ?? null;
}

/**
 * ── Broadcast Contacts API ────────────────────────────────────────────
 */
export async function getBroadcastContactsApi(params = {}) {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.category && params.category !== 'all') query.set('category', params.category);
  if (params.status && params.status !== 'all') query.set('status', params.status);
  // Superadmin only: 'all' or a user id. Omit for "my contacts" (the default).
  if (params.owner) query.set('owner', params.owner);
  if (params.sort_by) query.set('sort_by', params.sort_by);
  if (params.sort_dir) query.set('sort_dir', params.sort_dir);
  if (params.per_page !== undefined) query.set('per_page', params.per_page);
  if (params.page !== undefined) query.set('page', params.page);

  const qs = query.toString();
  const endpoint = `/broadcast/contacts${qs ? `?${qs}` : ''}`;
  return await apiFetch(endpoint, { method: 'GET' });
}

export async function getBroadcastContactsSummaryApi(params = {}) {
  const query = new URLSearchParams();
  if (params.owner) query.set('owner', params.owner);
  const qs = query.toString();
  const result = await apiFetch(`/broadcast/contacts/summary${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return result?.data ?? null;
}

/**
 * Superadmin: users a contact / contact set can be filtered by or moved to,
 * with how many contacts and sets each owns.
 */
export async function getBroadcastUsersApi() {
  const result = await apiFetch('/broadcast/users', { method: 'GET' });
  return result?.data ?? [];
}

/**
 * Superadmin: move contacts to another user. With `dryRun` the backend returns
 * the same report (moved / skipped / detached set memberships) without writing.
 */
export async function transferBroadcastContactsApi(contactIds, userId, { dryRun = false } = {}) {
  const result = await apiFetch('/broadcast/contacts/transfer', {
    method: 'POST',
    body: JSON.stringify({ contact_ids: contactIds, user_id: userId, dry_run: dryRun }),
  });
  return result?.data ?? null;
}

export async function getBroadcastContactDetailApi(id) {
  const result = await apiFetch(`/broadcast/contacts/${id}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function createBroadcastContactApi(payload) {
  return await apiFetch('/broadcast/contacts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateBroadcastContactApi(id, payload) {
  return await apiFetch(`/broadcast/contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteBroadcastContactApi(id) {
  return await apiFetch(`/broadcast/contacts/${id}`, {
    method: 'DELETE',
  });
}

export async function bulkDeleteBroadcastContactsApi(ids) {
  return await apiFetch('/broadcast/contacts/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export async function importCustomersToBroadcastContactsApi(category = 'Pelanggan') {
  return await apiFetch('/broadcast/contacts/import-customers', {
    method: 'POST',
    body: JSON.stringify({ category }),
  });
}

/**
 * Import contacts (e.g. from CSV). Each row may carry `priority` and `sets`
 * (set names; missing sets are created). The response's `data.rows` reports
 * each row's outcome in input order — created / existing / duplicate /
 * conflict / invalid. With `dryRun` nothing is written (used for the preview).
 */
export async function importBatchBroadcastContactsApi(contacts, defaultCategory = 'Umum', { dryRun = false } = {}) {
  return await apiFetch('/broadcast/contacts/import-batch', {
    method: 'POST',
    body: JSON.stringify({ contacts, default_category: defaultCategory, dry_run: dryRun }),
  });
}

// ── Broadcast Contact Sets API ─────────────────────────────────────────────

export async function getBroadcastContactSetsApi(params = {}) {
  const query = new URLSearchParams();
  // Superadmin only: 'all' or a user id. Omit for "my sets" (the default).
  if (params.owner) query.set('owner', params.owner);
  const qs = query.toString();
  const result = await apiFetch(`/broadcast/contact-sets${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return result?.data ?? [];
}

/**
 * Superadmin: move a contact set AND its member contacts to another user.
 * `dryRun` previews the impact; a phone conflict on the target skips the whole set.
 */
export async function transferBroadcastContactSetApi(setId, userId, { dryRun = false } = {}) {
  const result = await apiFetch(`/broadcast/contact-sets/${setId}/transfer`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, dry_run: dryRun }),
  });
  return result?.data ?? null;
}

export async function getBroadcastContactSetApi(id) {
  const result = await apiFetch(`/broadcast/contact-sets/${id}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function createBroadcastContactSetApi(payload) {
  return await apiFetch('/broadcast/contact-sets', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateBroadcastContactSetApi(id, payload) {
  return await apiFetch(`/broadcast/contact-sets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteBroadcastContactSetApi(id) {
  return await apiFetch(`/broadcast/contact-sets/${id}`, {
    method: 'DELETE',
  });
}

export async function getBroadcastContactSetContactsApi(id) {
  const result = await apiFetch(`/broadcast/contact-sets/${id}/contacts`, { method: 'GET' });
  return result?.data ?? [];
}

export async function syncBroadcastContactSetMembersApi(id, contactIds, mode = 'sync') {
  return await apiFetch(`/broadcast/contact-sets/${id}/members`, {
    method: 'POST',
    body: JSON.stringify({ contact_ids: contactIds, mode }),
  });
}

// ── Broadcast WhatsApp Groups API ──────────────────────────────────────────

export async function getBroadcastGroupsApi(params = {}) {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.status && params.status !== 'all') query.set('status', params.status);
  if (params.type && params.type !== 'all') query.set('type', params.type);
  if (params.sort_key) query.set('sort_key', params.sort_key);
  if (params.sort_dir) query.set('sort_dir', params.sort_dir);
  if (params.per_page !== undefined) query.set('per_page', params.per_page);
  if (params.page !== undefined) query.set('page', params.page);

  const qs = query.toString();
  const endpoint = `/broadcast/groups${qs ? `?${qs}` : ''}`;
  return await apiFetch(endpoint, { method: 'GET' });
}

export async function getBroadcastGroupsSummaryApi() {
  const result = await apiFetch('/broadcast/groups/summary', { method: 'GET' });
  return result?.data ?? null;
}

export async function crawlBroadcastGroupsApi() {
  return await apiFetch('/broadcast/groups/crawl', {
    method: 'POST',
  });
}

export async function getBroadcastGroupDetailApi(id) {
  const result = await apiFetch(`/broadcast/groups/${id}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function updateBroadcastGroupApi(id, payload) {
  return await apiFetch(`/broadcast/groups/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteBroadcastGroupApi(id) {
  return await apiFetch(`/broadcast/groups/${id}`, {
    method: 'DELETE',
  });
}

export async function bulkDeleteBroadcastGroupsApi(ids) {
  return await apiFetch('/broadcast/groups/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export async function bulkUpdateBroadcastGroupsStatusApi(ids, status) {
  return await apiFetch('/broadcast/groups/bulk-status', {
    method: 'POST',
    body: JSON.stringify({ ids, status }),
  });
}

// ── Broadcast WhatsApp Channels (@newsletter) API ──────────────────────────

/** Channels the paired device owns or admins, read live from the gateway. */
export async function getBroadcastChannelsApi() {
  return await apiFetch('/broadcast/channels', { method: 'GET' });
}

/** Rediscover channels from the connected phone; returns the refreshed list. */
export async function syncBroadcastChannelsApi() {
  return await apiFetch('/broadcast/channels/sync', {
    method: 'POST',
  });
}

/**
 * ── WhatsApp Message Dispatch API ─────────────────────────────────────
 */
/**
 * Which of `recipients` (phones or group/channel JIDs) already received this catalogue PDF
 * (`catalogueCode`) or these products (`itemIds`) in the last `days` days. Failed sends don't
 * count. Returns [{ recipient, last_sent_at, item_ids? }] for the matches only.
 */
export async function getRecentRecipientsApi({ recipients, days, catalogueCode, itemIds }) {
  const result = await apiFetch('/broadcast/messages/recent-recipients', {
    method: 'POST',
    body: JSON.stringify({
      recipients,
      days,
      ...(catalogueCode ? { catalogue_code: catalogueCode } : { item_ids: itemIds }),
    }),
  });
  return result?.data ?? [];
}

export async function sendWhatsAppMessageApi(payload) {
  return await apiFetch('/whatsapp/send', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Draft the WhatsApp caption variants for a catalogue PDF (nothing is sent).
 * With `sets` it drafts that many caption sets at once (for the batches of a batched send),
 * written to differ from the `avoid` texts.
 * @param {string} catalogueCode
 * @param {{ tones?: string[], noSugarGlaze?: boolean, customFocus?: string, sets?: number, avoid?: string[] }} options
 * @returns {Promise<{ variants?: Record<string, string>, sets?: Array<Record<string, string>>, ai: boolean }>}
 */
export async function draftCataloguePdfMessagesApi(catalogueCode, options = {}) {
  const result = await apiFetch(`/catalogue-new/pdf/${encodeURIComponent(catalogueCode)}/composite`, {
    method: 'POST',
    body: JSON.stringify({
      tones: options.tones,
      no_sugar_glaze: options.noSugarGlaze,
      custom_focus: options.customFocus || undefined,
      sets: options.sets,
      avoid: options.avoid?.length ? options.avoid : undefined,
    }),
  });
  return result?.data ?? null;
}

/**
 * Send the generated catalogue PDF to WhatsApp recipients. Each recipient gets one of the
 * `messages` variants at random; tones left out are written by the backend.
 * @param {string} catalogueCode
 * @param {Blob} blob PDF built on the client
 * @param {string} filename
 * @param {Array<{ phone: string, name?: string }>} recipients
 * @param {Record<string, string>} [messages] edited captions keyed by tone
 * @param {{ min: number, max: number }} [throttle] seconds the gateway waits between messages
 * @param {{ batches: Array<{ recipients: Array<{ phone: string, name?: string }>, messages: Record<string, string> }>, cooldown: { min: number, max: number } }} [batching]
 *   more than 15 recipients: pre-split batches, each with its confirmed captions, and the cooldown
 *   window in seconds between batches. `recipients` and `messages` are then ignored.
 * @returns {Promise<{ campaign_id: number, total: number, tones?: object, run_id?: number, batch_count?: number, run?: object }>}
 */
export async function sendCataloguePdfWhatsAppApi(
  catalogueCode,
  blob,
  filename,
  recipients,
  messages = {},
  throttle = null,
  batching = null,
) {
  const formData = new FormData();
  formData.append('file', new File([blob], filename, { type: 'application/pdf' }));
  if (batching) {
    batching.batches.forEach((batch, b) => {
      batch.recipients.forEach((recipient, i) => {
        formData.append(`batches[${b}][recipients][${i}][phone]`, recipient.phone);
        if (recipient.name) formData.append(`batches[${b}][recipients][${i}][name]`, recipient.name);
      });
      Object.entries(batch.messages).forEach(([tone, text]) => {
        formData.append(`batches[${b}][messages][${tone}]`, text);
      });
    });
    formData.append('cooldown_min_seconds', String(batching.cooldown.min));
    formData.append('cooldown_max_seconds', String(batching.cooldown.max));
  } else {
    recipients.forEach((recipient, i) => {
      formData.append(`recipients[${i}][phone]`, recipient.phone);
      if (recipient.name) formData.append(`recipients[${i}][name]`, recipient.name);
    });
    Object.entries(messages).forEach(([tone, text]) => {
      if (text && text.trim()) formData.append(`messages[${tone}]`, text);
    });
  }
  if (throttle) {
    formData.append('throttle_enabled', '1');
    formData.append('throttle_min_seconds', String(throttle.min));
    formData.append('throttle_max_seconds', String(throttle.max));
  }

  const result = await apiFetch(`/catalogue-new/pdf/${encodeURIComponent(catalogueCode)}`, {
    method: 'POST',
    body: formData,
  });
  return result?.data ?? null;
}

/**
 * The user's batched catalogue PDF send that is still going, or null. Reading it also moves it on.
 */
export async function getActiveCataloguePdfRunApi() {
  const result = await apiFetch('/catalogue-new/pdf/runs/active');
  return result?.data ?? null;
}

export async function getCataloguePdfRunApi(runId) {
  const result = await apiFetch(`/catalogue-new/pdf/runs/${runId}`);
  return result?.data ?? null;
}

/** Stop a batched send: the batch at the gateway is cancelled, the rest is never sent. */
export async function cancelCataloguePdfRunApi(runId) {
  const result = await apiFetch(`/catalogue-new/pdf/runs/${runId}/cancel`, { method: 'POST' });
  return result?.data ?? null;
}

/** Send a batch that failed to start again (e.g. after the WhatsApp device reconnected). */
export async function retryCataloguePdfRunApi(runId) {
  const result = await apiFetch(`/catalogue-new/pdf/runs/${runId}/retry`, { method: 'POST' });
  return result?.data ?? null;
}

/**
 * ── WhatsApp Message Logs API (Riwayat Pesan) ───────────────────────────
 */
export async function getWhatsAppMessageLogsApi(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      query.append(key, val);
    }
  });
  const qs = query.toString() ? `?${query.toString()}` : '';
  const result = await apiFetch(`/broadcast/messages${qs}`, { method: 'GET' });
  return result ?? { data: [], pagination: {}, kpis: {} };
}

export async function getWhatsAppMessageLogsSummaryApi() {
  const result = await apiFetch('/broadcast/messages/summary', { method: 'GET' });
  return result?.data ?? null;
}

export async function getWhatsAppMessageLogDetailApi(id) {
  const result = await apiFetch(`/broadcast/messages/${id}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function resendWhatsAppMessageLogApi(id) {
  return await apiFetch(`/broadcast/messages/${id}/resend`, {
    method: 'POST',
  });
}

export async function deleteWhatsAppMessageLogApi(id) {
  return await apiFetch(`/broadcast/messages/${id}`, {
    method: 'DELETE',
  });
}

export async function bulkDeleteWhatsAppMessageLogsApi(ids) {
  return await apiFetch('/broadcast/messages/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

/**
 * ── Broadcast Campaigns API (Riwayat Kampanye Broadcast) ────────────────
 */
export async function getBroadcastCampaignsApi(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      query.append(key, val);
    }
  });
  const qs = query.toString() ? `?${query.toString()}` : '';
  const result = await apiFetch(`/broadcast/campaigns${qs}`, { method: 'GET' });
  return result ?? { data: [], pagination: {}, kpis: {} };
}

export async function createBroadcastCampaignApi(payload) {
  return await apiFetch('/broadcast/campaigns', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getBroadcastCampaignDetailApi(id, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      query.append(key, val);
    }
  });
  const qs = query.toString() ? `?${query.toString()}` : '';
  const result = await apiFetch(`/broadcast/campaigns/${id}${qs}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function deleteBroadcastCampaignApi(id) {
  return await apiFetch(`/broadcast/campaigns/${id}`, {
    method: 'DELETE',
  });
}

/**
 * ── Realtime Broadcast Monitor API ─────────────────────────────────────
 */
export async function getBroadcastMonitorActiveApi() {
  const result = await apiFetch('/broadcast/monitor/active', { method: 'GET' });
  return result?.data ?? { has_active: false, active_count: 0, campaigns: [] };
}

export async function getBroadcastMonitorProgressApi(id) {
  const result = await apiFetch(`/broadcast/monitor/${id}/progress`, { method: 'GET' });
  return result?.data ?? null;
}

export async function stepBroadcastMonitorApi(id, batchSize = 1) {
  return await apiFetch(`/broadcast/monitor/${id}/step`, {
    method: 'POST',
    body: JSON.stringify({ batch_size: batchSize }),
  });
}

/**
 * ── Broadcast Reports & Analytics API ──────────────────────────────────
 */
/**
 * Download the broadcast report as a file. `format`: 'csv' (one row per
 * message), 'json' (full report + messages) or 'md' (readable summary).
 * Sent with the bearer header so the token never lands in a URL.
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export async function exportBroadcastReportApi(params = {}) {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/broadcast/reports/export${buildQueryString(params)}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const error = new Error(data?.message || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  const blob = await response.blob();
  const filename = filenameFromDisposition(response) ?? `laporan-broadcast-whatsapp.${params.format ?? 'csv'}`;
  return { blob, filename };
}

/** The report as a short WhatsApp-formatted summary (inline text, to copy into a chat). */
export async function getBroadcastReportWhatsAppTextApi(params = {}) {
  const { blob } = await exportBroadcastReportApi({ ...params, format: 'wa' });
  return blob.text();
}

export async function getBroadcastReportOverviewApi(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      query.append(key, val);
    }
  });
  const qs = query.toString() ? `?${query.toString()}` : '';
  const result = await apiFetch(`/broadcast/reports/overview${qs}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getBroadcastReportTimelineApi(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      query.append(key, val);
    }
  });
  const qs = query.toString() ? `?${query.toString()}` : '';
  const result = await apiFetch(`/broadcast/reports/timeline${qs}`, { method: 'GET' });
  return result?.data ?? [];
}

export async function getBroadcastReportDistributionApi(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      query.append(key, val);
    }
  });
  const qs = query.toString() ? `?${query.toString()}` : '';
  const result = await apiFetch(`/broadcast/reports/distribution${qs}`, { method: 'GET' });
  return result?.data ?? null;
}

export async function getBroadcastReportTopRecipientsApi(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      query.append(key, val);
    }
  });
  const qs = query.toString() ? `?${query.toString()}` : '';
  const result = await apiFetch(`/broadcast/reports/top-recipients${qs}`, { method: 'GET' });
  return result?.data ?? { contacts: [], groups: [] };
}





